import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchData } from 'services/api/useFetchData';
import { deleteData } from 'services/api/deleteData';
import { useGlobalContext } from 'GlobalContext';
import { useActiveFeature } from 'services/permissions/permissionChecks';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { canOrderPaidFeatures } from '../../billing/functions/billingUtils';
import { useModal } from 'components/modals/ModalContext';
import ApiTierOrderModal from '../../api/ApiTierOrderModal';
import ApiTierSetModal from '../../api/ApiTierSetModal';
import { IntegrationPageContentProps } from '../IntegrationPage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

// Shows and manages the isp vision api keys on the integration page
const IspVisionIntegrationPageContent: React.FC<IntegrationPageContentProps> = () => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const divisionsActive = useActiveFeature('divisions');
    const { environmentType, allowedRights } = useAuthContext();

    // Superusers and staff set the tier directly, without an order or payment
    const canSetTier = allowedRights.includes('no_user') || allowedRights.includes('staff_user');

    // Only environment types with a paid subscription can order api access
    const canUpgradeTier = canSetTier || canOrderPaidFeatures(environmentType);
    const [deletingIds, setDeletingIds] = useState<number[]>([]);

    // Fetch the isp vision api keys of the environment
    const { response: tokenList, refetch } = useFetchData({
        apiUrl: 'get_api_token_list',
        params: useMemo(() => ({ source: 'isp_vision' }), [])
    });
    const ispVisionKeys = tokenList?.results ?? [];

    // Delete an isp vision api key
    const deleteKey = async (tokenId: number) => {
        setDeletingIds(current => [...current, tokenId]);
        try {
            await deleteData({ apiUrl: `delete_api_token/${tokenId}` });
            refetch();
        } catch (error) {
            console.error('Error deleting isp vision api key', error);
            setFloatingAlert({ type: 'danger', message: t('integrations.isp_vision.key_delete_failed_error') });
        } finally {
            setDeletingIds(current => current.filter(id => id !== tokenId));
        }
    };

    // Open the tier set modal for superusers, otherwise the tier order modal to upgrade a free key
    const openTierOrderModal = (tokenId: number, currentTier: string) => {
        initializeModal(
            canSetTier
                ? <ApiTierSetModal tokenId={tokenId} currentTier={currentTier} onSuccess={refetch} />
                : <ApiTierOrderModal tokenId={tokenId} onSuccess={refetch} />,
            { modalSize: 'medium' }
        );
    };

    // Render nothing when there are no isp vision keys yet
    if (ispVisionKeys.length === 0) {
        return null;
    }

    return (
        <div className='integration-details'>
            <div className='isp-vision-keys'>
                <h6>{t('integrations.isp_vision.existing_keys_label')}</h6>
                {ispVisionKeys.map((token: any) => (
                    <div className='isp-vision-key-row' key={token.id}>
                        <span className='key-token'>{token.token}</span>
                        {divisionsActive && token.division_name && (
                            <span className='key-division'>{token.division_name}</span>
                        )}
                        <span className='key-tier'>
                            ({t(`api_key.tier.${token.rate_limit_tier}`)} {t('api_key.general.level_suffix')})
                        </span>
                        {(canSetTier || token.rate_limit_tier !== 'extended') && canUpgradeTier && (
                            <span
                                className='module-order-link upgrade-tier-link'
                                onClick={() => openTierOrderModal(token.id, token.rate_limit_tier)}
                            >
                                {t(canSetTier ? 'api_key.general.set_tier_label' : 'api_key.general.upgrade_tier_label')}
                            </span>
                        )}
                        <span
                            className={`key-delete ${deletingIds.includes(token.id) ? 'disabled' : ''}`}
                            title={t('general.delete')}
                            onClick={() => deleteKey(token.id)}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default IspVisionIntegrationPageContent;
